import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { MessageCircle, Phone, X, Check, Package, Store } from "lucide-react";
import { waLink, telLink } from "../lib/data";

const STEPS = {
  INTENT: 1,
  PRODUCTS: 2,
  FINAL: 3,
};

export const ContactGate = ({ open, onOpenChange, initialMessage }) => {
  const [step, setStep] = useState(STEPS.INTENT);
  const [intent, setIntent] = useState(null); // 'wholesale' | 'retail'
  const [interests, setInterests] = useState([]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep(STEPS.INTENT);
      setIntent(null);
      setInterests([]);
    }
  }, [open]);

  const toggleInterest = (val) => {
    setInterests((prev) =>
      prev.includes(val) ? prev.filter((i) => i !== val) : [...prev, val]
    );
  };

  const generateFinalMessage = () => {
    let msg = initialMessage || "Hello Mehdi Soap Center!";
    if (intent) {
      msg += `\nMain ${intent === "wholesale" ? "Wholesale/Bulk" : "Ghar ke liye (Retail)"} enquiry kar raha/rahi hoon.`;
    }
    if (interests.length > 0) {
      msg += `\nMujhe in products mein interest hai: ${interests.join(", ")}.`;
    }
    return msg;
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-gray-200 bg-white p-6 sm:p-8 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-2xl">
          <div className="absolute right-4 top-4">
            <Dialog.Close className="rounded-full p-2 hover:bg-gray-100 transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </Dialog.Close>
          </div>

          <Dialog.Title className="font-display font-black text-2xl text-[#1F2937]">
            {step === STEPS.INTENT && "Aap ki Enquiry Kis Liye Hai?"}
            {step === STEPS.PRODUCTS && "Aapko Kin Products Mein Interest Hai?"}
            {step === STEPS.FINAL && "Great! Ab Humse Connect Karein."}
          </Dialog.Title>

          <div className="mt-4">
            {step === STEPS.INTENT && (
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setIntent("wholesale");
                    setStep(STEPS.PRODUCTS);
                  }}
                  className="flex items-center gap-4 p-4 border-2 border-gray-100 hover:border-[#1E3A8A] rounded-xl text-left transition-colors group"
                >
                  <div className="w-12 h-12 shrink-0 bg-[#1E3A8A]/10 text-[#1E3A8A] rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Package className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1F2937]">Wholesale / Bulk Order</h4>
                    <p className="text-sm text-[#4B5563] mt-0.5">Dukaan ya factory ke liye bulk enquiry</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setIntent("retail");
                    setStep(STEPS.PRODUCTS);
                  }}
                  className="flex items-center gap-4 p-4 border-2 border-gray-100 hover:border-[#F59E0B] rounded-xl text-left transition-colors group"
                >
                  <div className="w-12 h-12 shrink-0 bg-[#F59E0B]/15 text-[#B45309] rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Store className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1F2937]">Retail / Ghar Ke Liye</h4>
                    <p className="text-sm text-[#4B5563] mt-0.5">Apne ghar ki safai ka saaman</p>
                  </div>
                </button>
              </div>
            )}

            {step === STEPS.PRODUCTS && (
              <div>
                <p className="text-sm text-[#4B5563] mb-4">
                  Sirf genuine buyers ke liye! Jo products aapko chahiye unhe select karein:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {["Handmade Soaps", "Branded Detergents", "Cleaning Equipment", "Phenyl & Liquids"].map((prod) => (
                    <button
                      key={prod}
                      onClick={() => toggleInterest(prod)}
                      className={`flex items-center justify-between p-3 border-2 rounded-lg text-sm font-semibold transition-colors ${
                        interests.includes(prod)
                          ? "border-[#1E3A8A] bg-[#1E3A8A]/5 text-[#1E3A8A]"
                          : "border-gray-100 text-[#4B5563] hover:border-gray-200"
                      }`}
                    >
                      {prod}
                      {interests.includes(prod) && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
                <div className="mt-6 flex justify-between">
                  <button
                    onClick={() => setStep(STEPS.INTENT)}
                    className="text-sm font-bold text-gray-500 hover:text-gray-800"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(STEPS.FINAL)}
                    className="btn-primary !min-h-[40px] !py-2"
                  >
                    Next Step
                  </button>
                </div>
              </div>
            )}

            {step === STEPS.FINAL && (
              <div>
                <p className="text-sm text-[#4B5563] mb-6">
                  Thank you! Humare paas daily hundreds of time-pass calls aate hain, isliye ye chhota sa process zaroori tha. Aap ka message ready hai, niche diye button par click karein.
                </p>
                <div className="flex flex-col gap-3">
                  <a
                    href={waLink(generateFinalMessage())}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => onOpenChange(false)}
                    className="btn-whatsapp w-full !h-14"
                  >
                    <MessageCircle className="w-5 h-5" /> WhatsApp Par Bhejein
                  </a>
                  <a
                    href={telLink}
                    onClick={() => onOpenChange(false)}
                    className="btn-outline w-full !h-14"
                  >
                    <Phone className="w-5 h-5" /> Direct Call Karein
                  </a>
                </div>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
